import { RouletteColour } from "./components/BetButtons/BetButtons";

export interface IRestoredBet {
    username: string;
    bet: number;
    potentialWin: number;
    selection: RouletteColour;
    spinId: string;
}

const RouletteColours: RouletteColour[] = [ "NONE", "RED", "GREEN", "BLACK" ];

const isRecord = (value: unknown): value is Record<string, unknown> => {
    return typeof value === "object" && value !== null;
};

const parseJson = (value: string): unknown => {
    try {
        return JSON.parse(value);
    } catch {
        return undefined;
    }
};

const isRouletteColour = (value: unknown): value is RouletteColour => {
    return typeof value === "string" && RouletteColours.includes(value as RouletteColour);
};

const toEntries = (placedBets: unknown): unknown[] => {
    if (Array.isArray(placedBets)) {
        return placedBets;
    }

    if (isRecord(placedBets)) {
        return Object.values(placedBets);
    }

    return [];
};

const getUsername = (entry: Record<string, unknown>, betDetails: Record<string, unknown>, fallbackUsername: string): string => {
    if (typeof entry.goosicornUsername === "string") {
        return entry.goosicornUsername;
    }

    if (typeof entry.username === "string") {
        return entry.username;
    }

    if (typeof betDetails.username === "string") {
        return betDetails.username;
    }

    return fallbackUsername;
};

const getBetAmount = (betDetails: Record<string, unknown>): number | undefined => {
    if (typeof betDetails.bet === "number") {
        return betDetails.bet;
    }

    if (typeof betDetails.amount === "number") {
        return betDetails.amount;
    }

    return undefined;
};

export const restorePlacedBets = (placedBets: unknown, fallbackUsername: string): IRestoredBet[] => {
    return toEntries(placedBets).flatMap(entry => {
        const parsedEntry = typeof entry === "string" ? parseJson(entry) : entry;
        if (!isRecord(parsedEntry)) {
            return [];
        }

        const gameAction = typeof parsedEntry.gameAction === "string" ? parseJson(parsedEntry.gameAction) : undefined;
        const betDetails = isRecord(gameAction) ? gameAction : parsedEntry;
        const username = getUsername(parsedEntry, betDetails, fallbackUsername);
        const bet = getBetAmount(betDetails);
        const selection = betDetails.selection;

        if (!username || !isRouletteColour(selection) || selection === "NONE" || typeof bet !== "number") {
            return [];
        }

        return [ {
            username,
            bet,
            potentialWin: typeof betDetails.potentialWin === "number" ? betDetails.potentialWin : 0,
            selection,
            spinId: typeof betDetails.spinId === "string" ? betDetails.spinId : ""
        } ];
    });
};

export const getStakeForPlayer = (placedBets: IRestoredBet[], username: string): number => {
    return placedBets.reduce((total, bet) => {
        if (bet.username !== username) {
            return total;
        }

        return total + bet.bet;
    }, 0);
};
